import { useMemo } from 'react';

import { DownloadModal } from './DownloadModal';
import { isBundleVariant, type DownloadDefinition } from './types';
import type { DownloadController } from './useDownloadController';
import { GenericExportProgressModal } from '@/new/components/dialogs/export-progress-dialog';

interface Props {
  definition: DownloadDefinition;
  controller: DownloadController;
}

interface ProgressConfig {
  progressEndpoint: string;
  statusEndpoint?: string;
  itemName: string;
  readyDescription?: string;
  downloadButtonText?: string;
  onDownload: () => Promise<void>;
}

export const DownloadFlow = ({ definition, controller }: Props) => {
  const activeAsync = controller.asyncExport;
  const activeVariant = useMemo(
    () => (activeAsync ? definition.variants.find((v) => v.id === activeAsync.variantId) : undefined),
    [activeAsync, definition.variants],
  );

  const progress = useMemo<ProgressConfig | null>(() => {
    if (!activeAsync || !activeVariant) return null;
    const { exportId } = activeAsync;

    if (isBundleVariant(activeVariant)) {
      const name = activeAsync.bundleName ?? activeVariant.bundle.defaultName;
      return {
        progressEndpoint: activeVariant.progressEndpoint(exportId),
        statusEndpoint: activeVariant.statusEndpoint?.(exportId),
        itemName: activeVariant.label,
        readyDescription: activeVariant.readyDescription,
        downloadButtonText: activeVariant.downloadButtonText,
        onDownload: () => activeVariant.downloadReady(exportId, name),
      };
    }

    if ('mode' in activeVariant && activeVariant.mode === 'async') {
      const ext = activeAsync.withSourceDocuments ? 'zip' : String(activeAsync.format);
      const downloadButtonText = activeAsync.filename
        ? `Download ${activeAsync.filename}.${ext}`
        : activeVariant.downloadButtonText;
      return {
        progressEndpoint: activeVariant.progressEndpoint(exportId),
        statusEndpoint: activeVariant.statusEndpoint?.(exportId),
        itemName: activeVariant.label,
        readyDescription: activeVariant.readyDescription,
        downloadButtonText,
        onDownload: () =>
          activeVariant.downloadReady(
            exportId,
            activeAsync.withSourceDocuments ? undefined : activeAsync.format,
            activeAsync.filename,
          ),
      };
    }

    return null;
  }, [activeAsync, activeVariant]);

  return (
    <>
      <DownloadModal
        open={controller.isOpen}
        onOpenChange={(o) => (o ? controller.open() : controller.close())}
        definition={definition}
        onAsyncStarted={controller.startAsync}
      />

      {activeAsync && progress && (
        <GenericExportProgressModal
          isOpen
          onClose={controller.dismissProgress}
          exportConfig={{
            progressEndpoint: progress.progressEndpoint,
            statusEndpoint: progress.statusEndpoint,
            exportId: activeAsync.exportId,
          }}
          itemName={progress.itemName}
          isCreating
          readyDescription={progress.readyDescription}
          downloadButtonText={progress.downloadButtonText}
          onDownload={async () => {
            await progress.onDownload();
            controller.dismissProgress();
          }}
        />
      )}
    </>
  );
};
